import type { EmployeeSummary } from "@/hooks/useMakersBoardData";
import type { AvailabilityMode } from "@/utils/makersUtils";
import { computeWeekColumns } from "@/utils/weekUtils";
import { useCallback, useMemo, useRef } from "react";
import { AvailabilityModeToggle } from "@/components/common/AvailabilityModeToggle";
import { RoleFilter } from "../filters/RoleFilter";
import { EmployeeRow } from "./EmployeeRow";
import { MakersWeekHeaderRow } from "./MakersWeekHeaderRow";

export type SortDirection = "asc" | "desc";

interface MakersTableProps {
  employees: EmployeeSummary[];
  startDate: Date;
  numWeeks: number;
  roleOptions: string[];
  sortDirection: SortDirection;
  toggleSortDirection: () => void;
  selectedRoles: string[];
  onToggleRole: (role: string) => void;
  onClearRoleFilter: () => void;
  updateCapacityOptimistically: (
    employeeId: string,
    newCapacity: number,
  ) => void;
  updateEmployeeCapacity: (
    employeeId: string,
    newCapacity: number,
  ) => Promise<void>;
  updateEmployeeRoles: (
    employeeId: string,
    roles: import("@/hooks/useMakersBoardData").EmployeeRole[],
  ) => Promise<void>;
  updateRolesOptimistically: (
    employeeId: string,
    roles: import("@/hooks/useMakersBoardData").EmployeeRole[],
  ) => void;
  updateNameOptimistically: (employeeId: string, newName: string) => void;
  updateEmployeeName: (employeeId: string, newName: string) => Promise<void>;
  updateNicknameOptimistically: (employeeId: string, nickname: string | null) => void;
  updateEmployeeNickname: (employeeId: string, nickname: string | null) => Promise<void>;
  onEmployeeClick?: (employeeId: string) => void;
  selectedEmployeeIds?: Set<string>;
  roleEditingDisabled?: boolean;
  availabilityMode?: AvailabilityMode;
  onAvailabilityModeChange?: (mode: AvailabilityMode) => void;
  onDeleteEmployee?: (employeeId: string) => Promise<void>;
  showDeleteForManual?: boolean;
}

/**
 * Table component displaying employees with sortable columns
 */
export function MakersTable({
  employees,
  roleOptions,
  selectedRoles,
  onToggleRole,
  onClearRoleFilter,
  startDate,
  numWeeks,
  sortDirection,
  toggleSortDirection,
  updateCapacityOptimistically,
  updateEmployeeCapacity,
  updateEmployeeRoles,
  updateRolesOptimistically,
  updateNameOptimistically,
  updateEmployeeName,
  updateNicknameOptimistically,
  updateEmployeeNickname,
  onEmployeeClick,
  selectedEmployeeIds = new Set(),
  roleEditingDisabled = false,
  availabilityMode = "all",
  onAvailabilityModeChange,
  onDeleteEmployee,
  showDeleteForManual = false,
}: MakersTableProps) {
  const weeks = useMemo(
    () => computeWeekColumns(startDate, numWeeks),
    [employees, startDate, numWeeks],
  );

  const weekHeaderTimelineRef = useRef<HTMLDivElement>(null);

  const handleBodyScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    if (weekHeaderTimelineRef.current) {
      weekHeaderTimelineRef.current.scrollLeft = e.currentTarget.scrollLeft;
    }
  }, []);

  return (
    <div className="makers-table">
      <div className="makers-table__header">
        <div className="makers-table__fixed-header">
          {onAvailabilityModeChange && (
            <div className="makers-board__toggle makers-board__toggle--mode">
              Show
              <AvailabilityModeToggle
                mode={availabilityMode}
                onChange={onAvailabilityModeChange}
              />
            </div>
          )}
        </div>
        <div className="makers-table__timeline-header">
          <RoleFilter
            roles={roleOptions}
            selected={selectedRoles}
            onToggle={onToggleRole}
            onClear={onClearRoleFilter}
          />
        </div>
      </div>
      <MakersWeekHeaderRow
        weeks={weeks}
        sortDirection={sortDirection}
        onSort={toggleSortDirection}
        timelineRef={weekHeaderTimelineRef}
      />
      <div className="makers-table__body-scroll" onScroll={handleBodyScroll}>
        <div className="makers-table__body">
          {employees.length === 0 ? (
            <div className="makers-table__empty">
              No employees found matching the current filters.
            </div>
          ) : (
            employees.map((employee) => (
              <EmployeeRow
                key={employee.id}
                employee={employee}
                weeks={weeks}
                roleOptions={roleOptions}
                updateCapacityOptimistically={updateCapacityOptimistically}
                updateEmployeeCapacity={updateEmployeeCapacity}
                updateEmployeeRoles={updateEmployeeRoles}
                updateRolesOptimistically={updateRolesOptimistically}
                onClick={onEmployeeClick}
                isSelected={selectedEmployeeIds.has(employee.id)}
                roleEditingDisabled={roleEditingDisabled}
                availabilityMode={availabilityMode}
                onDelete={
                  showDeleteForManual && !employee.employee_number
                    ? onDeleteEmployee
                    : undefined
                }
                onRename={
                  showDeleteForManual && !employee.employee_number
                    ? updateEmployeeName
                    : undefined
                }
                updateNameOptimistically={updateNameOptimistically}
                onNicknameChange={
                  showDeleteForManual && employee.employee_number
                    ? updateEmployeeNickname
                    : undefined
                }
                updateNicknameOptimistically={updateNicknameOptimistically}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}
